// VELLA 2.0 — CREATIVE EDITOR
// Full-screen canvas editor for AI-generated creatives. Two stacked
// canvases — one shows the (cropped) source image, one captures the
// inpaint mask the user paints with the brush. Text overlays float on
// top as positioned divs the user can drag, and get composited onto
// the canvas at save time.
//
// Inpainting flow:
//   1. user paints over a region with the brush → mask canvas accumulates
//      white strokes on transparent background.
//   2. "Regenerate masked area" serializes the image canvas + mask canvas
//      as base64 PNGs and POSTs them to /api/creative/inpaint with the
//      regen prompt the user types in.
//   3. backend returns { result: base64 } — that becomes the new source
//      image and the mask is cleared.
//
// Built by AMPLIFX Advertising Agency

import React, { useState, useEffect, useRef, useCallback } from "react";
import * as API from "../api";
import { FONT, BLUE, OCEAN_3, DANGER } from "../shared";

const MIN_BRUSH = 5;
const MAX_BRUSH = 50;

// Tool ids — kept as constants so the toolbar buttons + active-state
// comparisons can't drift from the canvas event branches.
const TOOL_BRUSH = "brush";
const TOOL_ERASER = "eraser";
const TOOL_TEXT = "text";
const TOOL_CROP = "crop";

// Glassmorphic dark-mode toolbar surface — same blur recipe as the
// Vella light theme but tinted to read on the dark editor backdrop.
const GLASS_DARK = {
  background: "linear-gradient(135deg, rgba(20,30,50,0.78) 0%, rgba(15,25,45,0.62) 100%)",
  backdropFilter: "blur(28px) saturate(180%)",
  WebkitBackdropFilter: "blur(28px) saturate(180%)",
  border: "1px solid rgba(255,255,255,0.12)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10), 0 18px 44px -18px rgba(0,0,0,0.55)",
  color: "#E2E8F0",
};

const GLASS_BTN = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "#E2E8F0",
  borderRadius: 10,
  padding: 8,
  cursor: "pointer",
  fontFamily: FONT,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  transition: "background 0.15s ease, border-color 0.15s ease",
};

const GLASS_BTN_ACTIVE = {
  ...GLASS_BTN,
  background: `linear-gradient(135deg, ${OCEAN_3} 0%, ${BLUE} 100%)`,
  border: "1px solid rgba(255,255,255,0.30)",
  color: "#FFF",
};

function Icon({ name, size = 18 }) {
  const props = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "brush":  return <svg {...props}><path d="M9.06 11.9l8.07-8.06a2.85 2.85 0 114.03 4.03l-8.06 8.08"/><path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 00-3-3.02z"/></svg>;
    case "eraser": return <svg {...props}><path d="M20 20H7L3 16c-.39-.39-.39-1.02 0-1.41L13.59 4 20 10.41 16.41 14"/><path d="M18 13l-6-6"/></svg>;
    case "text":   return <svg {...props}><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>;
    case "crop":   return <svg {...props}><path d="M6 2v14a2 2 0 0 0 2 2h14"/><path d="M18 22V8a2 2 0 0 0-2-2H2"/></svg>;
    case "undo":   return <svg {...props}><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-15-6.7L3 13"/></svg>;
    case "redo":   return <svg {...props}><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 15-6.7L21 13"/></svg>;
    case "reset":  return <svg {...props}><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>;
    case "close":  return <svg {...props}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
    case "check":  return <svg {...props}><polyline points="20 6 9 17 4 12"/></svg>;
    case "sparkle":return <svg {...props}><path d="M12 3l1.7 4.7L18 9.4l-4.3 1.7L12 16l-1.7-4.9L6 9.4l4.3-1.7z"/><path d="M5 19l.7 1.7L7 21l-1.3.3L5 23l-.7-1.7L3 21l1.3-.3z"/></svg>;
    default:       return null;
  }
}

// Convert a canvas to a base64 PNG without the "data:image/png;base64," prefix.
// Returns null when the canvas is tainted by a cross-origin source — caller
// must surface a friendly error rather than blowing up the whole flow.
function canvasToBase64(canvas) {
  try {
    return canvas.toDataURL("image/png").split(",")[1] || null;
  } catch {
    return null;
  }
}

export default function CreativeEditor({ open, imageUrl, initialPrompt, onClose, onSave }) {
  const wrapRef = useRef(null);
  const imageCanvasRef = useRef(null);
  const maskCanvasRef = useRef(null);
  const sourceImageRef = useRef(null); // HTMLImageElement for the original (post-load)
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const cropDragRef = useRef(null); // { corner, startX, startY, startCrop }
  const textDragRef = useRef(null); // { id, offsetX, offsetY }

  const [tool, setTool] = useState(TOOL_BRUSH);
  const [brushSize, setBrushSize] = useState(20);
  const [brushColor, setBrushColor] = useState("#FFFFFF");
  const [overlays, setOverlays] = useState([]);   // [{ id, text, x, y, fontSize, color }]
  const [crop, setCrop] = useState(null);         // { x, y, w, h } in canvas pixels
  const [history, setHistory] = useState([]);     // snapshots for undo
  const [redoStack, setRedoStack] = useState([]); // snapshots for redo
  const [imageDims, setImageDims] = useState({ width: 0, height: 0 });
  const [displayDims, setDisplayDims] = useState({ width: 0, height: 0, scale: 1 });
  const [regenPrompt, setRegenPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [taintedSource, setTaintedSource] = useState(false);

  // ── Snapshot helpers ─────────────────────────────────────────
  // We capture the image-canvas pixels + the overlay/crop state. The
  // mask canvas is intentionally NOT snapshotted — undo wipes the mask
  // because partial-stroke undo is fiddlier than the user expects.
  const snapshot = useCallback(() => {
    const img = imageCanvasRef.current;
    if (!img) return null;
    return {
      imageData: img.toDataURL("image/png"),
      overlays: overlays.map((o) => ({ ...o })),
      crop: crop ? { ...crop } : null,
    };
  }, [overlays, crop]);

  const pushHistory = useCallback(() => {
    const snap = snapshot();
    if (!snap) return;
    setHistory((h) => [...h.slice(-19), snap]); // cap at 20 snapshots
    setRedoStack([]);
  }, [snapshot]);

  // ── Load source image + size canvases ────────────────────────
  useEffect(() => {
    if (!open || !imageUrl) return;
    setError(null);
    setTaintedSource(false);
    setOverlays([]);
    setCrop(null);
    setHistory([]);
    setRedoStack([]);

    const img = new Image();
    img.crossOrigin = "anonymous"; // required for toDataURL on remote URLs
    img.onload = () => {
      sourceImageRef.current = img;
      setImageDims({ width: img.naturalWidth, height: img.naturalHeight });

      // Wait one frame so the canvas refs exist with their real size.
      requestAnimationFrame(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        // Fit-to-viewport scale — leave headroom for the toolbars.
        const maxW = wrap.clientWidth - 40;
        const maxH = wrap.clientHeight - 40;
        const scale = Math.min(1, maxW / img.naturalWidth, maxH / img.naturalHeight);
        const dispW = Math.round(img.naturalWidth * scale);
        const dispH = Math.round(img.naturalHeight * scale);
        setDisplayDims({ width: dispW, height: dispH, scale });

        // Image canvas: matches natural dimensions so mask coordinates
        // sent to the backend stay 1:1 with the original. Display sizes
        // are CSS-only.
        const ic = imageCanvasRef.current;
        const mc = maskCanvasRef.current;
        if (!ic || !mc) return;
        ic.width = img.naturalWidth;
        ic.height = img.naturalHeight;
        mc.width = img.naturalWidth;
        mc.height = img.naturalHeight;
        const ictx = ic.getContext("2d");
        ictx.clearRect(0, 0, ic.width, ic.height);
        ictx.drawImage(img, 0, 0);
        const mctx = mc.getContext("2d");
        mctx.clearRect(0, 0, mc.width, mc.height);

        // Initial snapshot so the very first undo brings us back here.
        setHistory([{
          imageData: ic.toDataURL("image/png"),
          overlays: [],
          crop: null,
        }]);
      });
    };
    img.onerror = () => setError("Couldn't load that image. Try another creative.");
    img.src = imageUrl;
  }, [open, imageUrl]);

  // ── Pointer → canvas coordinates ─────────────────────────────
  // The mask + image canvases share the natural dimensions, so we
  // multiply the on-screen offset by the inverse of the display scale.
  const toCanvasCoords = useCallback((evt) => {
    const mc = maskCanvasRef.current;
    if (!mc) return { x: 0, y: 0 };
    const rect = mc.getBoundingClientRect();
    const x = (evt.clientX - rect.left) * (mc.width / rect.width);
    const y = (evt.clientY - rect.top) * (mc.height / rect.height);
    return { x, y };
  }, []);

  // ── Brush / eraser drawing ───────────────────────────────────
  const startDraw = useCallback((evt) => {
    if (tool !== TOOL_BRUSH && tool !== TOOL_ERASER) return;
    const mc = maskCanvasRef.current;
    if (!mc) return;
    drawingRef.current = true;
    lastPointRef.current = toCanvasCoords(evt);
    pushHistory();
    // Anchor a dot so a single tap leaves a visible mark.
    drawSegment(lastPointRef.current, lastPointRef.current);
  }, [tool, toCanvasCoords, pushHistory]);

  const drawSegment = useCallback((from, to) => {
    const mc = maskCanvasRef.current;
    if (!mc) return;
    const ctx = mc.getContext("2d");
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    // Brush size scales with the natural-pixel canvas so a 20px brush
    // covers a believable area on a 4k creative as well as a 1080 one.
    ctx.lineWidth = brushSize * (mc.width / Math.max(displayDims.width, 1));
    if (tool === TOOL_ERASER) {
      ctx.globalCompositeOperation = "destination-out";
      ctx.strokeStyle = "rgba(0,0,0,1)";
    } else {
      ctx.globalCompositeOperation = "source-over";
      // Brush color drives the visual; for the inpaint mask we'll
      // re-render to pure white at submit time so the brush color
      // doesn't leak into the API call.
      ctx.strokeStyle = brushColor;
    }
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  }, [tool, brushSize, brushColor, displayDims.width]);

  const moveDraw = useCallback((evt) => {
    if (!drawingRef.current) return;
    const cur = toCanvasCoords(evt);
    drawSegment(lastPointRef.current, cur);
    lastPointRef.current = cur;
  }, [drawSegment, toCanvasCoords]);

  const endDraw = useCallback(() => {
    drawingRef.current = false;
    lastPointRef.current = null;
  }, []);

  // ── Undo / redo / reset ──────────────────────────────────────
  const restoreSnapshot = useCallback((snap) => {
    if (!snap) return;
    const img = new Image();
    img.onload = () => {
      const ic = imageCanvasRef.current;
      const mc = maskCanvasRef.current;
      if (!ic || !mc) return;
      const ictx = ic.getContext("2d");
      ictx.clearRect(0, 0, ic.width, ic.height);
      ictx.drawImage(img, 0, 0);
      const mctx = mc.getContext("2d");
      mctx.clearRect(0, 0, mc.width, mc.height);
      setOverlays(snap.overlays || []);
      setCrop(snap.crop || null);
    };
    img.src = snap.imageData;
  }, []);

  const handleUndo = useCallback(() => {
    if (history.length <= 1) return;
    const cur = snapshot();
    setRedoStack((r) => [...r, cur].slice(-20));
    const prev = history[history.length - 2];
    setHistory((h) => h.slice(0, -1));
    restoreSnapshot(prev);
  }, [history, snapshot, restoreSnapshot]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack((r) => r.slice(0, -1));
    const cur = snapshot();
    setHistory((h) => [...h, cur].slice(-20));
    restoreSnapshot(next);
  }, [redoStack, snapshot, restoreSnapshot]);

  const handleReset = useCallback(() => {
    const orig = sourceImageRef.current;
    const ic = imageCanvasRef.current;
    const mc = maskCanvasRef.current;
    if (!orig || !ic || !mc) return;
    const ictx = ic.getContext("2d");
    ictx.clearRect(0, 0, ic.width, ic.height);
    ictx.drawImage(orig, 0, 0);
    const mctx = mc.getContext("2d");
    mctx.clearRect(0, 0, mc.width, mc.height);
    setOverlays([]);
    setCrop(null);
    setHistory([{ imageData: ic.toDataURL("image/png"), overlays: [], crop: null }]);
    setRedoStack([]);
  }, []);

  // ── Text overlays ────────────────────────────────────────────
  const addTextOverlay = useCallback(() => {
    pushHistory();
    setOverlays((arr) => [
      ...arr,
      {
        id: `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        text: "Double-click to edit",
        // Center on the canvas in display coordinates.
        x: displayDims.width / 2 - 80,
        y: displayDims.height / 2 - 12,
        fontSize: 28,
        color: brushColor,
      },
    ]);
  }, [pushHistory, displayDims, brushColor]);

  const startTextDrag = (evt, ov) => {
    if (tool !== TOOL_TEXT) return;
    evt.stopPropagation();
    textDragRef.current = {
      id: ov.id,
      offsetX: evt.clientX - ov.x,
      offsetY: evt.clientY - ov.y,
    };
  };
  const moveTextDrag = (evt) => {
    const d = textDragRef.current;
    if (!d) return;
    setOverlays((arr) =>
      arr.map((o) =>
        o.id === d.id ? { ...o, x: evt.clientX - d.offsetX, y: evt.clientY - d.offsetY } : o,
      ),
    );
  };
  const endTextDrag = () => { textDragRef.current = null; };

  const editTextOverlay = (id) => {
    const cur = overlays.find((o) => o.id === id);
    if (!cur) return;
    const next = window.prompt("Text overlay:", cur.text);
    if (next === null) return;
    pushHistory();
    setOverlays((arr) => arr.map((o) => (o.id === id ? { ...o, text: next } : o)));
  };

  const removeTextOverlay = (id) => {
    pushHistory();
    setOverlays((arr) => arr.filter((o) => o.id !== id));
  };

  // ── Crop tool ────────────────────────────────────────────────
  const startCrop = useCallback((evt) => {
    if (tool !== TOOL_CROP) return;
    const { x, y } = toCanvasCoords(evt);
    pushHistory();
    setCrop({ x, y, w: 1, h: 1 });
    cropDragRef.current = { corner: "br", startX: x, startY: y, startCrop: { x, y, w: 1, h: 1 } };
  }, [tool, toCanvasCoords, pushHistory]);

  const moveCrop = useCallback((evt) => {
    const d = cropDragRef.current;
    if (!d) return;
    const { x, y } = toCanvasCoords(evt);
    const x0 = Math.min(d.startX, x);
    const y0 = Math.min(d.startY, y);
    const w = Math.abs(x - d.startX);
    const h = Math.abs(y - d.startY);
    setCrop({ x: x0, y: y0, w, h });
  }, [toCanvasCoords]);

  const endCrop = () => { cropDragRef.current = null; };

  const applyCrop = useCallback(() => {
    if (!crop || crop.w < 4 || crop.h < 4) return;
    const ic = imageCanvasRef.current;
    if (!ic) return;
    pushHistory();
    const ctx = ic.getContext("2d");
    const data = ctx.getImageData(crop.x, crop.y, crop.w, crop.h);
    ic.width = crop.w;
    ic.height = crop.h;
    ic.getContext("2d").putImageData(data, 0, 0);
    // Mask must follow.
    const mc = maskCanvasRef.current;
    mc.width = crop.w;
    mc.height = crop.h;
    setImageDims({ width: crop.w, height: crop.h });
    // Re-fit display.
    const wrap = wrapRef.current;
    if (wrap) {
      const maxW = wrap.clientWidth - 40;
      const maxH = wrap.clientHeight - 40;
      const scale = Math.min(1, maxW / crop.w, maxH / crop.h);
      setDisplayDims({ width: Math.round(crop.w * scale), height: Math.round(crop.h * scale), scale });
    }
    setCrop(null);
  }, [crop, pushHistory]);

  // ── Inpaint API call ─────────────────────────────────────────
  const handleInpaint = useCallback(async () => {
    setError(null);
    const ic = imageCanvasRef.current;
    const mc = maskCanvasRef.current;
    if (!ic || !mc) return;

    // Build a pure black/white mask: white where the user painted,
    // black elsewhere. We do this off-screen so the visible brush
    // color stays whatever the user picked.
    const tmp = document.createElement("canvas");
    tmp.width = mc.width;
    tmp.height = mc.height;
    const tctx = tmp.getContext("2d");
    tctx.fillStyle = "#000";
    tctx.fillRect(0, 0, tmp.width, tmp.height);
    tctx.globalCompositeOperation = "source-over";
    // Re-tint the mask to white wherever it has any alpha.
    tctx.drawImage(mc, 0, 0);
    const id = tctx.getImageData(0, 0, tmp.width, tmp.height);
    for (let i = 0; i < id.data.length; i += 4) {
      const a = id.data[i + 3];
      if (a > 8) {
        id.data[i] = 255; id.data[i + 1] = 255; id.data[i + 2] = 255; id.data[i + 3] = 255;
      } else {
        id.data[i] = 0;   id.data[i + 1] = 0;   id.data[i + 2] = 0;   id.data[i + 3] = 255;
      }
    }
    tctx.putImageData(id, 0, 0);

    const imageB64 = canvasToBase64(ic);
    const maskB64 = canvasToBase64(tmp);
    if (!imageB64 || !maskB64) {
      setTaintedSource(true);
      setError("This image's source blocks editing (CORS). Regenerate it from the prompt and try again.");
      return;
    }
    // Reject empty masks — saves a backend round-trip.
    let nonZero = 0;
    for (let i = 3; i < id.data.length && nonZero < 1; i += 4) {
      if (id.data[i - 3] === 255) nonZero = 1;
    }
    if (!nonZero) {
      setError("Paint over the area you want to regenerate first.");
      return;
    }

    setBusy(true);
    try {
      const res = await API.inpaintCreative({
        image: imageB64,
        mask: maskB64,
        prompt: regenPrompt || initialPrompt || "",
      });
      const result = res?.result;
      if (!result) throw new Error("No result returned by the inpaint API.");

      // Replace the source image with the regenerated one and clear the mask.
      const next = new Image();
      next.onload = () => {
        sourceImageRef.current = next;
        ic.width = next.naturalWidth;
        ic.height = next.naturalHeight;
        mc.width = next.naturalWidth;
        mc.height = next.naturalHeight;
        ic.getContext("2d").drawImage(next, 0, 0);
        mc.getContext("2d").clearRect(0, 0, mc.width, mc.height);
        setImageDims({ width: next.naturalWidth, height: next.naturalHeight });
        const wrap = wrapRef.current;
        if (wrap) {
          const maxW = wrap.clientWidth - 40;
          const maxH = wrap.clientHeight - 40;
          const scale = Math.min(1, maxW / next.naturalWidth, maxH / next.naturalHeight);
          setDisplayDims({
            width: Math.round(next.naturalWidth * scale),
            height: Math.round(next.naturalHeight * scale),
            scale,
          });
        }
        pushHistory();
        setBusy(false);
      };
      next.onerror = () => { setError("Server returned a result we couldn't decode."); setBusy(false); };
      next.src = result.startsWith("data:") ? result : `data:image/png;base64,${result}`;
    } catch (err) {
      setError(err.message || "Inpaint request failed.");
      setBusy(false);
    }
  }, [regenPrompt, initialPrompt, pushHistory]);

  // ── Save (composite text overlays onto the canvas) ───────────
  const handleSave = useCallback(() => {
    const ic = imageCanvasRef.current;
    if (!ic) return;
    const out = document.createElement("canvas");
    out.width = ic.width;
    out.height = ic.height;
    const ctx = out.getContext("2d");
    ctx.drawImage(ic, 0, 0);

    // Map overlay display coordinates back into canvas coordinates.
    const scale = ic.width / Math.max(displayDims.width, 1);
    ctx.textBaseline = "top";
    overlays.forEach((o) => {
      ctx.fillStyle = o.color || "#FFFFFF";
      ctx.font = `${Math.max(8, Math.round(o.fontSize * scale))}px ${FONT}`;
      ctx.fillText(o.text, o.x * scale, o.y * scale);
    });

    let dataUrl = "";
    try {
      dataUrl = out.toDataURL("image/png");
    } catch {
      setError("Couldn't export the image (CORS). Regenerate first, then save.");
      return;
    }
    onSave?.(dataUrl);
    onClose?.();
  }, [overlays, displayDims.width, onSave, onClose]);

  // ── Pointer dispatch ─────────────────────────────────────────
  const onPointerDown = (evt) => {
    if (tool === TOOL_BRUSH || tool === TOOL_ERASER) startDraw(evt);
    else if (tool === TOOL_CROP) startCrop(evt);
  };
  const onPointerMove = (evt) => {
    if (drawingRef.current) moveDraw(evt);
    else if (cropDragRef.current) moveCrop(evt);
    else if (textDragRef.current) moveTextDrag(evt);
  };
  const onPointerUp = () => {
    endDraw();
    endCrop();
    endTextDrag();
  };

  // Esc closes (only when not editing a text overlay).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); handleUndo(); }
      else if ((e.metaKey || e.ctrlKey) && (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey))) { e.preventDefault(); handleRedo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, handleUndo, handleRedo]);

  if (!open) return null;

  const ToolBtn = ({ id, label, onClick, icon, disabled }) => {
    const isActive = id && tool === id;
    return (
      <button
        type="button"
        onClick={onClick || (() => setTool(id))}
        disabled={disabled}
        title={label}
        style={{
          ...(isActive ? GLASS_BTN_ACTIVE : GLASS_BTN),
          opacity: disabled ? 0.4 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
          minWidth: 40,
          height: 40,
        }}
      >
        <Icon name={icon} />
      </button>
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(135deg, rgba(6,11,25,0.96) 0%, rgba(10,20,40,0.94) 100%)",
        fontFamily: FONT,
        color: "#E2E8F0",
        userSelect: "none",
      }}
    >
      {/* Top bar */}
      <div
        style={{
          ...GLASS_DARK,
          margin: 14,
          padding: "10px 14px",
          borderRadius: 16,
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon name="brush" size={16} />
          <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: "-0.01em" }}>Creative editor</span>
        </div>
        <div style={{ width: 1, height: 22, background: "rgba(255,255,255,0.10)" }} />

        <ToolBtn id={TOOL_BRUSH}  label="Brush"  icon="brush" />
        <ToolBtn id={TOOL_ERASER} label="Eraser" icon="eraser" />
        <ToolBtn id={TOOL_TEXT}   label="Text"   icon="text" onClick={() => { setTool(TOOL_TEXT); addTextOverlay(); }} />
        <ToolBtn id={TOOL_CROP}   label="Crop"   icon="crop" />

        <div style={{ width: 1, height: 22, background: "rgba(255,255,255,0.10)" }} />

        {/* Brush size */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: "rgba(226,232,240,0.7)", letterSpacing: 0.4, textTransform: "uppercase" }}>Size</span>
          <input
            type="range"
            min={MIN_BRUSH}
            max={MAX_BRUSH}
            value={brushSize}
            onChange={(e) => setBrushSize(Number(e.target.value))}
            style={{ accentColor: BLUE, width: 110 }}
          />
          <span style={{ fontSize: 11, color: "#E2E8F0", minWidth: 28, textAlign: "right" }}>{brushSize}px</span>
        </div>

        {/* Color picker */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: "rgba(226,232,240,0.7)", letterSpacing: 0.4, textTransform: "uppercase" }}>Color</span>
          <input
            type="color"
            value={brushColor}
            onChange={(e) => setBrushColor(e.target.value)}
            style={{
              width: 36,
              height: 28,
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.18)",
              background: "transparent",
              cursor: "pointer",
            }}
          />
        </div>

        <div style={{ flex: 1 }} />

        <ToolBtn label="Undo"  onClick={handleUndo}  icon="undo"  disabled={history.length <= 1} />
        <ToolBtn label="Redo"  onClick={handleRedo}  icon="redo"  disabled={redoStack.length === 0} />
        <ToolBtn label="Reset" onClick={handleReset} icon="reset" />

        <button
          type="button"
          onClick={onClose}
          title="Close"
          style={{ ...GLASS_BTN, width: 40, height: 40 }}
        >
          <Icon name="close" />
        </button>
      </div>

      {/* Canvas wrapper */}
      <div
        ref={wrapRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        style={{
          flex: 1,
          margin: "0 14px",
          borderRadius: 16,
          ...GLASS_DARK,
          position: "relative",
          overflow: "hidden",
          touchAction: "none", // pointer events drive drawing on touch
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {error && (
          <div
            style={{
              position: "absolute",
              top: 16,
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(239,68,68,0.18)",
              border: `1px solid ${DANGER}`,
              color: "#FCA5A5",
              borderRadius: 10,
              padding: "8px 14px",
              fontSize: 12,
              maxWidth: "80%",
              textAlign: "center",
              zIndex: 5,
            }}
          >
            {error}
          </div>
        )}

        {/* Canvas stack */}
        <div
          style={{
            position: "relative",
            width: displayDims.width,
            height: displayDims.height,
            boxShadow: "0 24px 48px -16px rgba(0,0,0,0.55)",
            borderRadius: 8,
            overflow: "hidden",
            background: "#000",
          }}
        >
          <canvas
            ref={imageCanvasRef}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
          />
          <canvas
            ref={maskCanvasRef}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              display: "block",
              opacity: 0.5,
              pointerEvents: "none",
              mixBlendMode: "screen",
            }}
          />

          {/* Crop rectangle */}
          {crop && crop.w > 0 && crop.h > 0 && displayDims.width > 0 && (() => {
            const cs = displayDims.width / Math.max(imageDims.width, 1);
            return (
              <div
                style={{
                  position: "absolute",
                  left: crop.x * cs,
                  top: crop.y * cs,
                  width: crop.w * cs,
                  height: crop.h * cs,
                  border: `2px dashed ${OCEAN_3}`,
                  background: "rgba(56,189,248,0.10)",
                  pointerEvents: "none",
                }}
              />
            );
          })()}

          {/* Text overlays */}
          {overlays.map((o) => (
            <div
              key={o.id}
              onPointerDown={(e) => startTextDrag(e, o)}
              onDoubleClick={() => editTextOverlay(o.id)}
              onContextMenu={(e) => { e.preventDefault(); removeTextOverlay(o.id); }}
              style={{
                position: "absolute",
                left: o.x,
                top: o.y,
                cursor: tool === TOOL_TEXT ? "move" : "default",
                pointerEvents: tool === TOOL_TEXT ? "auto" : "none",
                color: o.color,
                fontSize: o.fontSize,
                fontWeight: 700,
                fontFamily: FONT,
                textShadow: "0 2px 12px rgba(0,0,0,0.55)",
                padding: "2px 6px",
                border: tool === TOOL_TEXT ? "1px dashed rgba(255,255,255,0.45)" : "none",
                borderRadius: 4,
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              {o.text}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div
        style={{
          ...GLASS_DARK,
          margin: 14,
          padding: 14,
          borderRadius: 16,
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <input
          type="text"
          placeholder="What should the masked area become? (optional prompt)"
          value={regenPrompt}
          onChange={(e) => setRegenPrompt(e.target.value)}
          style={{
            flex: 1,
            minWidth: 220,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 10,
            padding: "10px 14px",
            color: "#E2E8F0",
            fontSize: 13,
            fontFamily: FONT,
            outline: "none",
          }}
        />

        {tool === TOOL_CROP && crop && (
          <button
            type="button"
            onClick={applyCrop}
            style={{
              ...GLASS_BTN_ACTIVE,
              padding: "10px 18px",
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 10,
            }}
          >
            <Icon name="check" /> Apply crop
          </button>
        )}

        <button
          type="button"
          onClick={handleInpaint}
          disabled={busy || taintedSource}
          style={{
            ...GLASS_BTN_ACTIVE,
            padding: "10px 18px",
            fontSize: 13,
            fontWeight: 600,
            borderRadius: 10,
            opacity: busy || taintedSource ? 0.5 : 1,
            cursor: busy || taintedSource ? "not-allowed" : "pointer",
          }}
        >
          <Icon name="sparkle" />
          {busy ? "Regenerating…" : "Regenerate masked area"}
        </button>

        <button
          type="button"
          onClick={onClose}
          style={{
            ...GLASS_BTN,
            padding: "10px 18px",
            fontSize: 13,
            fontWeight: 600,
            borderRadius: 10,
          }}
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handleSave}
          style={{
            background: `linear-gradient(135deg, ${OCEAN_3} 0%, ${BLUE} 60%, #0284C7 100%)`,
            color: "#FFF",
            border: "1px solid rgba(255,255,255,0.30)",
            padding: "10px 22px",
            fontSize: 13,
            fontWeight: 700,
            borderRadius: 10,
            cursor: "pointer",
            fontFamily: FONT,
            boxShadow: "0 10px 24px -8px rgba(14,165,233,0.45)",
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
}
